<template>
  <div id="bubblesCanvas" ref="bubblesCanvas" />
</template>

<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component'
import Konva from 'konva'
import {
  Bubble,
  BubblesSimulation,
  BubblesTransitionOperations,
  fadeInBubbleGroup,
  fadeOutBubbleGroup,
  getVisPosition,
  shouldBubblesAmalgamate,
  transformCoordinatesBubblesToScreen,
  updateBubbleGroupProperties,
  RenderedBubbleGroup,
  bubblesShapesService,
  BubblesInteractions,
} from '@/components/public/Visualisations/Bubbles'
import { Coordinate2D, NodeGroup, VisNode } from '@/types'
import { PATH_ROOT, defaultBubblePosition } from '@/constants'
import _debounce from 'lodash/debounce'

interface BubbleAmalgamation {
  ancestor: Bubble
  descendants: Bubble[]
}

// eslint-disable-next-line no-use-before-define
@Component<BubblesCanvas>({
  watch: {
    storyPath() {
      this.storyPathUpdated()
    },
    isRoot() {
      this.updateSimulationConfig()
    },
    config: {
      handler() {
        this.configUpdated()
      },
      deep: true,
    },
    nodeGroups() {
      this.nodeGroupsUpdated()
    },
    bubblesContainerDimensions() {
      this.debouncedHandleResize()
    },
  },
})
export default class BubblesCanvas extends Vue {
  private simulation: BubblesSimulation | undefined
  private stage: Konva.Stage | undefined
  private layer: Konva.Layer | undefined
  private interactions: BubblesInteractions | undefined
  private pulseAnimations: Record<string, Konva.Animation> = {}
  // to improve rendering performance these properties are non-reactive
  private bubblesVisualisationScaleFactor!: number
  private bubblesMinSizeSquareOffset!: Coordinate2D

  private pendingBubbleAmalgamations: BubbleAmalgamation[] = []
  private pendingBubblesToRemove: Bubble[] = []

  created() {
    this.bubblesVisualisationScaleFactor = 1
    this.bubblesMinSizeSquareOffset = { x: 0, y: 0 }
  }

  mounted() {
    this.initialise()
  }

  private initialise() {
    this.initialiseCanvas()
    this.initialiseInteractions()
    this.initialiseBubbles()
    this.initialiseSimulation()
  }

  private initialiseCanvas() {
    this.stage = new Konva.Stage({
      container: 'bubblesCanvas',
      width: this.bubblesCanvas.clientWidth,
      height: this.bubblesCanvas.clientHeight,
    })

    this.layer = new Konva.Layer()

    this.stage.add(this.layer)
  }

  private initialiseInteractions() {
    this.interactions = new BubblesInteractions(this.stage!, this.$analytics)
    this.interactions.on('transformView', () => {
      this.updateTransformationProperties()
      this.updateBubbleGroupsProperties()
      this.updateSimulation()
    })
  }

  private initialiseBubbles() {
    bubblesShapesService.reset()

    const bubbles = this.allVisNodes.map(
      (visNode) => new Bubble(visNode, this.config, this.storyPath),
    )

    // Dampen the initial burst by randomly scattering
    // the bubbles over a wider area
    bubbles.forEach((bubble: Bubble) => {
      bubble.x = Math.floor(Math.random() * 201) - 100
      bubble.y = Math.floor(Math.random() * 201) - 100
    })

    this.addBubblesToCanvas(bubbles)
    this.updateBubbleGroupsProperties()
    this.updateBubblesDestinationPositions()
  }

  private initialiseSimulation() {
    this.simulation = new BubblesSimulation(this.isRoot)
    this.simulation.on('tick', this.simulationTicked)
    this.simulation.on('end', this.simulationEnded)
    this.updateSimulation()
  }

  get storyPath() {
    return this.$typedStore.visualisationData.storyPath
  }

  get isRoot() {
    return this.storyPath.isRoot
  }

  get nodeGroups() {
    return this.$typedStore.visualisationData.nodeGroups
  }

  get allVisNodes(): VisNode[] {
    return [...this.nodeGroups.main.flatMap((g) => g.nodes), ...this.nodeGroups.side]
  }

  get config() {
    return this.$typedStore.visualisationData.bubblesConfig
  }

  get bubblePositions() {
    return this.$typedStore.visualisationData.bubblePositions
  }

  get bubblesCanvas() {
    return this.$refs.bubblesCanvas as HTMLElement
  }

  handleResize() {
    this.updateTransformationProperties()

    const currentPixelRation = window.devicePixelRatio || 1
    if (Konva.pixelRatio !== currentPixelRation) {
      Konva.pixelRatio = currentPixelRation
      this.initialise()
    } else {
      this.interactions?.reset()
      this.updateBubbleGroupsProperties()
      this.stage?.width(this.bubblesContainerDimensions.width)
      this.stage?.height(this.bubblesContainerDimensions.height)
      this.updateSimulation()
    }
  }

  debouncedHandleResize = _debounce(this.handleResize, 250, { leading: true, maxWait: 250 })

  // Cache the values required for transforming Bubbles coordinates
  // so we don't have to access the store for every rendered circle
  private updateTransformationProperties() {
    this.bubblesVisualisationScaleFactor =
      this.$typedStore.public.size.bubblesVisualisationScaleFactor
    this.bubblesMinSizeSquareOffset = {
      x: this.$typedStore.public.size.bubblesMinSizeSquareOffset.x,
      y: this.$typedStore.public.size.bubblesMinSizeSquareOffset.y,
    }
  }

  get bubblesContainerDimensions() {
    return this.$typedStore.public.size.bubblesContainerDimensions
  }

  get viewerLevel() {
    return this.$typedStore.visualisationData.viewerLevel
  }

  get mainLevel() {
    return this.$typedStore.visualisationData.mainLevel
  }

  get clusterHeadersHash() {
    return this.$typedStore.visualisationData.clusterHeadersHash
  }

  configUpdated() {
    if (this.simulation) {
      this.updateBubblesConfig()
      this.updateBubbleGroupsProperties()
      this.updateBubblesDestinationPositions()
      this.updateSimulationConfig()
    }
  }

  storyPathUpdated() {
    if (this.simulation) {
      this.interactions?.reset()
      this.updateTransformationProperties()
      this.updateBubblesActiveStoryPath()
      this.updateBubbleGroupsProperties()
      this.updateBubblesDestinationPositions()
    }
  }

  nodeGroupsUpdated() {
    const nodeIds = new Set<string>()
    const bubbleOperations = new BubblesTransitionOperations()

    this.allVisNodes.forEach((visNode: VisNode) => {
      const visNodeId = visNode.id
      nodeIds.add(visNodeId)
      const bubble = bubblesShapesService.getBubble(visNodeId)
      if (bubble) {
        bubble.updateNode(visNode)
      } else {
        const bubble = new Bubble(visNode, this.config, this.storyPath)
        bubbleOperations.appendBubbleToAdd(bubble)
      }
    })

    this.pendingBubblesToRemove = []
    for (const bubble of Object.values(bubblesShapesService.bubbles)) {
      if (!nodeIds.has(bubble.id)) {
        bubbleOperations.appendBubbleToRemove(bubble)
      }
    }

    this.removeBubblesFromCanvas(bubbleOperations.toRemove)

    for (const bubble of bubbleOperations.toAdd) {
      bubble.destinationPosition = this.determineBubbleDestinationPosition(bubble)
    }
    this.addBubblesToCanvas(bubbleOperations.toAdd)

    for (const operations of bubbleOperations.amalgamating) {
      this.handleBubbleAmalgamating(operations.add, operations.remove)
    }

    for (const operations of bubbleOperations.splitting) {
      this.handleBubbleSplitting(operations.add, operations.remove)
    }

    this.updateBubbleGroupsProperties()
    this.updateSimulation()
  }

  private handleBubbleSplitting(descendantBubbles: Bubble[], bubbleToSplit: Bubble) {
    if (descendantBubbles.length) {
      // Determine the destination of the first descendant Bubble then update the destination
      // position for all of the Bubbles, including the Bubble that is going to be split
      bubbleToSplit.destinationPosition = this.determineBubbleDestinationPosition(
        descendantBubbles[0],
      )
      descendantBubbles.forEach((descendant: Bubble) => {
        descendant.x = bubbleToSplit.x
        descendant.y = bubbleToSplit.y
        descendant.destinationPosition = this.determineBubbleDestinationPosition(descendant)
      })

      this.addBubblesToCanvas(descendantBubbles)
      this.removeBubblesFromCanvas([bubbleToSplit])
      this.updateBubbleGroupsProperties()

      this.updateSimulation()
    }
  }

  /*
   * When descendant bubbles are amalgamating into their common ancestor we want
   * the descendant bubbles to move towards the new ancestor position then
   * when enough of them are close to that position replace the descendant bubbles
   * with the ancestor bubble, this should give the illusion that the bubbles are merging together.
   * To achieve this we create the ancestor bubble but do not render it yet. We then
   * set the descendant bubbles' destination positions to the ancestor bubble's
   * destination position and update the simulation to start moving the descendant bubbles
   * together.
   * As the simulation ticks we then calculate whether enough of them are near their destination,
   * if enough of them are then we remove the descendant bubbles from the canvas and render the ancestor bubble
   */
  private handleBubbleAmalgamating(amalgamatedBubble: Bubble, descendantBubbles: Bubble[]) {
    amalgamatedBubble.destinationPosition =
      this.determineBubbleDestinationPosition(amalgamatedBubble)
    amalgamatedBubble.x = amalgamatedBubble.destinationPosition.x
    amalgamatedBubble.y = amalgamatedBubble.destinationPosition.y

    descendantBubbles.forEach((descendant: Bubble) => {
      descendant.destinationPosition = amalgamatedBubble.destinationPosition
    })

    this.pendingBubbleAmalgamations.push({
      ancestor: amalgamatedBubble,
      descendants: descendantBubbles,
    })
    this.pendingBubblesToRemove.push(...descendantBubbles)
  }

  private amalgamateBubbles(bubbleAmalgamation: BubbleAmalgamation) {
    this.pendingBubbleAmalgamations = this.pendingBubbleAmalgamations.filter(
      (x) => x.ancestor !== bubbleAmalgamation.ancestor,
    )

    this.addBubblesToCanvas([bubbleAmalgamation.ancestor])
    this.removeBubblesFromCanvas(bubbleAmalgamation.descendants)
    this.updateBubbleGroupsProperties()

    this.updateSimulation()
  }

  private simulationTicked() {
    for (const bubbleAmalgamation of this.pendingBubbleAmalgamations) {
      if (shouldBubblesAmalgamate(bubbleAmalgamation.descendants)) {
        this.amalgamateBubbles(bubbleAmalgamation)
      }
    }
  }

  private simulationEnded() {
    // Amalgamate any Bubbles that should have already been amalgamated but haven't for whatever reason
    for (const bubbleAmalgamation of this.pendingBubbleAmalgamations) {
      this.amalgamateBubbles(bubbleAmalgamation)
    }
    // Remove any Bubbles that should have already been removed but haven't
    this.removeBubblesFromCanvas(this.pendingBubblesToRemove)
    this.pendingBubblesToRemove = []
  }

  private updateBubblesConfig() {
    const bubbles = Object.values(bubblesShapesService.bubbles)
    bubbles.forEach((bubble: Bubble) => {
      bubble.updateConfig(this.config)
    })
  }

  private updateBubblesActiveStoryPath() {
    const bubbles = Object.values(bubblesShapesService.bubbles)
    bubbles.forEach((bubble: Bubble) => {
      bubble.updateActiveStoryPath(this.storyPath)
    })
  }

  private updateSimulationConfig() {
    if (this.simulation) {
      this.simulation.updateIsRoot(this.isRoot)
      this.updateSimulation()
    }
  }

  private updateSimulation() {
    this.simulation!.updateSimulation(bubblesShapesService.bubbles)
  }

  private addBubblesToCanvas(bubbles: Bubble[]) {
    bubbles.forEach((bubble: Bubble) => {
      // The pulse and halo circles have to be added first so the
      // mouseenter event on the bubble circle can fire
      const pulseCircle = bubblesShapesService.buildBubblePulseCircle(bubble)
      this.layer?.add(pulseCircle)

      const haloCircle = bubblesShapesService.buildBubbleHaloCircle(bubble)
      this.layer?.add(haloCircle)

      const bubbleCircle = bubblesShapesService.buildBubbleCircle(bubble, bubble.visNode)
      this.layer?.add(bubbleCircle)

      const bubbleGroup = {
        bubbleCircle,
        haloCircle,
        pulseCircle,
      } as RenderedBubbleGroup

      // When the d3 simulation updates the Bubbles coordinates we also need to update the
      // Konva Circles, the most efficient way to do this is to pass a callback to each Bubble
      // that will update the Circle coordinates every time the Bubble coordinates are updated
      bubble.onPositionChanged(this.updateBubbleGroupCoordinates(bubbleGroup))

      fadeInBubbleGroup(bubbleGroup)

      // When a simulation is already running and Bubbles are waiting to
      // amalgamate we may already have a Bubble rendered for the given path,
      // we need to ensure that it is removed before attempting to render the new bubble
      this.removeBubblesFromCanvas([bubble])

      bubblesShapesService.addBubble(bubble.id, bubble)
      bubblesShapesService.addBubbleGroup(bubble.id, bubbleGroup)
    })
  }

  private removeBubblesFromCanvas(bubbles: Bubble[]) {
    bubbles.forEach((bubble: Bubble) => {
      const bubbleGroup = bubblesShapesService.getBubbleGroup(bubble.id)
      if (bubbleGroup) {
        fadeOutBubbleGroup(bubbleGroup)
      }

      // If the active Bubbles Popup is for the bubble being removed
      // then clear the Popup
      if (this.activeBubblesPopupPath && this.activeBubblesPopupPath.equals(bubble.path)) {
        this.$typedStore.activeVisualisation.setActiveBubblesPopupDetails(undefined)
      }

      bubblesShapesService.removeBubbleGroup(bubble.id)
      bubblesShapesService.removeBubble(bubble.id)
    })
  }

  private get activeBubblesPopupPath() {
    return this.$typedStore.activeVisualisation.activeBubblesPopupDetails?.node?.path
  }

  private updateBubbleGroupsProperties() {
    bubblesShapesService.bubbleGroups.forEach((group: RenderedBubbleGroup) => {
      if (group) {
        updateBubbleGroupProperties(
          group,
          this.pulseAnimations,
          this.bubblesVisualisationScaleFactor,
        )

        // Refresh the bubble group coordinates
        this.updateBubbleGroupCoordinates(group)
      }
    })
  }

  private updateBubbleGroupCoordinates(group: RenderedBubbleGroup) {
    return (position: Coordinate2D) => {
      const transformedPosition = transformCoordinatesBubblesToScreen(
        position,
        this.bubblesVisualisationScaleFactor,
        this.bubblesMinSizeSquareOffset,
      )

      const { bubbleCircle, haloCircle, pulseCircle } = group
      const { renderedBubble } = bubbleCircle
      bubbleCircle.x(transformedPosition.x)
      bubbleCircle.y(transformedPosition.y)

      if (renderedBubble.halo.show) {
        haloCircle.x(transformedPosition.x)
        haloCircle.y(transformedPosition.y)
      }

      if (renderedBubble.pulse.show) {
        pulseCircle.x(transformedPosition.x)
        pulseCircle.y(transformedPosition.y)
      }
    }
  }

  private get mainBubblesAncestorPaths(): Record<string, string> {
    const paths = this.nodeGroups.main.flatMap((group: NodeGroup) =>
      group.nodes.map((node: VisNode) => [node.path, group.ancestorPath]),
    )

    return Object.fromEntries(paths)
  }

  private get sideBubblesPaths(): Set<string> {
    return new Set(this.nodeGroups.side.map((node) => node.path.toString()))
  }

  private determineBubbleDestinationPosition(bubble: Bubble) {
    if (this.isRoot) {
      return getVisPosition(
        PATH_ROOT,
        this.config.bubblePositions,
        this.viewerLevel,
        this.clusterHeadersHash,
      )
    }
    if (this.storyPath.isSubroot) {
      return getVisPosition(
        bubble.level1,
        this.config.bubblePositions,
        this.viewerLevel,
        this.clusterHeadersHash,
      )
    }
    const bubblePath = bubble.path.toString()
    if (bubblePath in this.mainBubblesAncestorPaths) {
      const ancestorPath = this.mainBubblesAncestorPaths[bubblePath]
      return getVisPosition(
        ancestorPath,
        this.config.bubblePositions,
        this.viewerLevel,
        this.clusterHeadersHash,
      )
    }
    if (this.sideBubblesPaths.has(bubblePath)) {
      return {
        x: this.config.bubbleParams.sideBubblePositionX,
        y: this.config.bubbleParams.sideBubblePositionY,
      }
    }
    return defaultBubblePosition()
  }

  private updateBubblesDestinationPositions() {
    bubblesShapesService.bubbles.forEach((bubble: Bubble) => {
      bubble.destinationPosition = this.determineBubbleDestinationPosition(bubble)
    })
  }
}
</script>

<style>
#bubblesCanvas {
  position: absolute;
  width: 100%;
  height: calc(100% - 1px) !important;
  touch-action: none;
  user-select: none;
}
</style>
